'use client';

import { useEffect, useState } from 'react';
import { ChevronRight, Download, ExternalLink, HandHeart } from 'lucide-react';
import { SheetPage } from '@church/nextjs-ui/components/ResponsiveSheet';
import type { Giving, GivingStatement } from '@/lib/types';
import { isUnauthorized } from '@/lib/api';
import { fmtShortDate, programLabel } from '@/lib/format';
import { consumeOpenParam, withOpenParam } from '@/lib/openParam';
import { useProfile } from './ProfileContext';
import { MonthlyBars } from './GivingChart';
import {
  AdjustPage,
  CommitmentsPage,
  CommitmentsSummary,
  useCommitments,
} from './CommitmentsSection';
import {
  Bar,
  CARD,
  CTA_SMALL,
  CTA_TEAL,
  DISPLAY,
  FactCard,
  HideAmountsToggle,
  Money,
  PageBody,
  Pii,
  ProfileSheet,
} from './ui';

/**
 * Giving as one card: the year's total said big, how many gifts, the last
 * one, the month chart, and under a hairline what has been promised (the
 * commitments; a commitment is about future giving, so it lives here). Tap
 * anywhere and the Giving sheet opens with the years as chips, the
 * statement PDF, the commitments as full cards (Adjust drills in) and
 * every gift of the year with its program split. Give goes to the giving
 * page; Manage to OLG's donor sign-in, where recurring
 * gifts and payment methods live. `?open=giving` on the page opens the
 * sheet on arrival (the login drawer links there).
 */
/** Year chips shown before "+N more" in the Giving sheet. */
const RECENT_YEARS = 5;

export function GivingCard() {
  const { api, urls } = useProfile();
  const commitments = useCommitments();
  const { giving, setYear, switching } = useGiving();
  const [open, setOpen] = useState(() => consumeOpenParam('giving'));
  const [statements, setStatements] = useState<GivingStatement[]>([]);
  const [allYears, setAllYears] = useState(false);

  // The statements MP has produced, one PDF per year; a missing one is not an error.
  useEffect(() => {
    const controller = new AbortController();
    api
      .getGivingStatements(controller.signal)
      .then((list) => {
        if (!controller.signal.aborted) setStatements(list);
      })
      .catch(() => {});
    return () => controller.abort();
  }, [api]);

  const last = giving && giving !== 'error' ? giving.donations[0] : undefined;
  // Year chips: every year with gifts OR a statement. A statement can exist for
  // a year the household's donor records show no gifts in (gifts on another
  // record that year), and it must still be reachable.
  const years =
    giving && giving !== 'error'
      ? Array.from(new Set([...giving.years, ...statements.map((st) => st.year)])).sort(
          (a, b) => b - a
        )
      : [];
  // The newest RECENT_YEARS chips, the rest behind "+N more". A selected year
  // older than those stays visible, so the sheet never hides where you are.
  const shownYears =
    allYears || years.length <= RECENT_YEARS
      ? years
      : years.filter(
          (y, i) => i < RECENT_YEARS || (giving && giving !== 'error' && y === giving.year)
        );
  const hiddenYears = years.length - shownYears.length;
  // Usually one per year, but MP can produce more (a regenerated or split
  // statement) — offer every one rather than the first.
  const yearStatements =
    giving && giving !== 'error' ? statements.filter((st) => st.year === giving.year) : [];

  return (
    <>
      <FactCard
        title="Giving"
        icon={<HandHeart className="h-4 w-4" strokeWidth={2.5} />}
        onEdit={() => setOpen(true)}
        editLabel="Open"
        // `overflow-clip`, not hidden: hidden makes the card a scroll container, and the glow that
        // hangs past the corner would let a focus or a scroll-into-view shove the content sideways.
        className="relative overflow-clip"
      >
        {giving === null ? (
          <div className="flex flex-col gap-2" aria-busy="true">
            <Bar className="h-3 w-24" />
            <Bar className="h-12 w-40" />
          </div>
        ) : giving === 'error' ? (
          <p className="text-sm text-gray-500">We couldn't load your giving right now.</p>
        ) : giving.years.length === 0 ? (
          <p className="text-sm text-gray-500">
            No gifts on record yet. Gifts you give online or at the campus show up here, by year.
          </p>
        ) : (
          <div className="relative">
            <span
              aria-hidden
              className="pointer-events-none absolute -top-16 -right-12 h-44 w-44 rounded-full opacity-20 blur-3xl"
              style={{ background: 'radial-gradient(closest-side, #299c8f, transparent)' }}
            />
            <div className="relative flex items-center justify-between gap-3">
              <p className="text-[11px] font-bold tracking-widest text-gray-500 uppercase">
                Given in {giving.year}
              </p>
              <HideAmountsToggle />
            </div>
            <p className={`${DISPLAY} relative mt-1 text-5xl leading-none text-[#1c2b39]`}>
              <Money value={giving.total} />
            </p>
            <p className="relative mt-2 text-sm text-gray-500">
              {giving.donations.length === 0
                ? 'Nothing yet this year.'
                : `${giving.donations.length} ${giving.donations.length === 1 ? 'gift' : 'gifts'}${
                    last ? ` · last on ${fmtShortDate(last.date)}` : ''
                  }`}
            </p>
            {giving.donations.length > 0 && (
              <MonthlyBars
                donations={giving.donations}
                year={giving.year}
                className="relative mt-3"
              />
            )}
            <div className="mt-3">
              <CommitmentsSummary c={commitments} year={giving.year} />
            </div>
          </div>
        )}
        {/* Give first and full width, Manage under it as the secondary. The
            statement PDF is in the sheet. */}
        <div className="mt-auto flex flex-col gap-2">
          <a href={urls.give} onClick={(e) => e.stopPropagation()} className={`${CTA_TEAL} w-full`}>
            Give
            <ChevronRight className="h-4 w-4" strokeWidth={2.5} />
          </a>
          <ManageGivingLink className="w-full" />
        </div>
      </FactCard>

      <ProfileSheet
        open={open}
        onClose={() => {
          setOpen(false);
          commitments.setEditing(null);
        }}
        title="Giving"
        maxWidth="max-w-2xl"
      >
        <SheetPage name="main">
          <PageBody>
            {giving && giving !== 'error' && years.length > 0 && (
              <div className="flex flex-wrap items-center gap-2">
                {shownYears.map((y) => (
                  <button
                    key={y}
                    type="button"
                    aria-pressed={y === giving.year}
                    onClick={() => setYear(y)}
                    className={`rounded-full px-3 py-1.5 text-sm font-semibold transition-all hover:scale-105 active:scale-95 ${
                      y === giving.year
                        ? 'bg-[#1c2b39] text-white shadow-md'
                        : 'bg-gray-100 text-[#1c2b39] hover:bg-gray-200'
                    }`}
                  >
                    {y}
                  </button>
                ))}
                {(hiddenYears > 0 || allYears) && years.length > RECENT_YEARS && (
                  <button
                    type="button"
                    aria-expanded={allYears}
                    onClick={() => setAllYears((v) => !v)}
                    className="rounded-full px-3 py-1.5 text-sm font-semibold text-[#299c8f] transition-all hover:bg-gray-100 active:scale-95"
                  >
                    {allYears ? 'Fewer' : `+${hiddenYears} more`}
                  </button>
                )}
                <span className="ml-auto">
                  <HideAmountsToggle />
                </span>
                {giving.donations.length > 0 && (
                  <span className="text-sm font-bold text-[#1c2b39]">
                    <Money value={giving.total} />
                    <span className="font-normal text-gray-500"> in {giving.year}</span>
                  </span>
                )}
              </div>
            )}
            {giving && giving !== 'error' && years.length > 0 && (
              <div
                className={`${CARD} flex flex-col gap-3 p-4 transition-opacity ${switching ? 'opacity-50' : ''}`}
              >
                {giving.donations.length > 0 ? (
                  <MonthlyBars donations={giving.donations} year={giving.year} />
                ) : (
                  <p className="text-sm text-gray-500">Nothing in {giving.year}.</p>
                )}
                <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-100 pt-3">
                  <span className="text-xs text-gray-500">
                    {yearStatements.length === 0
                      ? `No ${giving.year} statement yet. Statements are produced after the year closes.`
                      : yearStatements.length === 1
                        ? `Your ${giving.year} statement from ${yearStatements[0].company ?? 'Woodside'}, as a PDF.`
                        : `Your ${yearStatements.length} statements for ${giving.year}, as PDFs.`}
                  </span>
                  {yearStatements.length > 0 && (
                    <span className="flex flex-wrap gap-2">
                      {yearStatements.map((st, i) => (
                        <a
                          key={st.statementId}
                          href={st.url}
                          target="_blank"
                          rel="noopener"
                          className={`${CTA_SMALL} bg-[#1c2b39] text-white`}
                        >
                          <Download className="h-4 w-4" strokeWidth={2.5} />
                          {yearStatements.length === 1
                            ? 'Statement (PDF)'
                            : `Statement ${i + 1} (PDF)`}
                        </a>
                      ))}
                    </span>
                  )}
                </div>
              </div>
            )}
            {giving && giving !== 'error' && <CommitmentsPage c={commitments} year={giving.year} />}
            {giving === null ? (
              <div className="flex flex-col gap-3" aria-busy="true">
                <Bar className="h-10 w-full rounded-2xl" />
                <Bar className="h-10 w-full rounded-2xl" />
              </div>
            ) : giving === 'error' ? (
              <p className="text-sm text-gray-500">We couldn't load your giving right now.</p>
            ) : giving.years.length === 0 || giving.donations.length === 0 ? (
              <p className="text-sm text-gray-500">
                {giving.years.length === 0
                  ? 'No gifts on record yet.'
                  : `Nothing in ${giving.year}.`}
              </p>
            ) : (
              <div className={`${CARD} overflow-hidden`}>
                <div
                  className={`divide-y divide-gray-100 transition-opacity ${switching ? 'opacity-50' : ''}`}
                >
                  {giving.donations.map((d) => (
                    <div key={d.donationId} className="flex items-start gap-3 px-5 py-3">
                      <span className="w-16 shrink-0 text-sm text-gray-500">
                        {fmtShortDate(d.date)}
                      </span>
                      <span className="min-w-0 flex-1 text-sm text-[#1c2b39]">
                        {d.distributions.length === 1 ? (
                          <span className="font-semibold">
                            {programLabel(d.distributions[0].program)}
                          </span>
                        ) : (
                          d.distributions.map((x, i) => (
                            <span key={i} className="block">
                              <span className="font-semibold">{programLabel(x.program)}</span>
                              <span className="text-gray-500">
                                {' '}
                                · <Money value={x.amount} />
                              </span>
                            </span>
                          ))
                        )}
                        {d.paymentType && (
                          <span className="block text-xs text-gray-400">{d.paymentType}</span>
                        )}
                      </span>
                      <span className="shrink-0 text-sm font-bold text-[#1c2b39] tabular-nums">
                        <Money value={d.amount} />
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
              <a href={urls.give} className={CTA_TEAL}>
                Give
                <ChevronRight className="h-4 w-4" strokeWidth={2.5} />
              </a>
              <ManageGivingLink />
            </div>
          </PageBody>
        </SheetPage>
        <SheetPage name="adjust" title="Adjust">
          {commitments.editing && (
            <AdjustPage
              key={commitments.editing.commitmentId}
              card={commitments.editing}
              seeJesus={commitments.isSeeJesus(commitments.editing.campaignId)}
              onSave={(patch) => commitments.save(commitments.editing!, patch)}
              onRemove={() => commitments.remove(commitments.editing!)}
            />
          )}
        </SheetPage>
      </ProfileSheet>
    </>
  );
}

/**
 * The year's giving, fetched once per selected year. `year` undefined means
 * the newest year with gifts; a 401 reads as no gifts, not as an error.
 */
export function useGiving() {
  const { api } = useProfile();
  const [year, setYear] = useState<number | undefined>(undefined);
  const [giving, setGiving] = useState<Giving | null | 'error'>(null);
  const [switching, setSwitching] = useState(false);
  useEffect(() => {
    const controller = new AbortController();
    setSwitching(true);
    api
      .getGiving(year, controller.signal)
      .then((g) => {
        if (!controller.signal.aborted) setGiving(g);
      })
      .catch((err) => {
        if (controller.signal.aborted) return;
        setGiving(
          isUnauthorized(err)
            ? { years: [], year: new Date().getFullYear(), total: 0, donations: [] }
            : 'error'
        );
      })
      .finally(() => {
        if (!controller.signal.aborted) setSwitching(false);
      });
    return () => controller.abort();
  }, [api, year]);
  return { giving, setYear, switching };
}

/** The CTA's shape in outline: the secondary beside a filled Give. */
const CTA_OUTLINE =
  'inline-flex items-center justify-center gap-1.5 rounded-full px-6 py-3 text-base font-bold text-[#299c8f] ring-2 ring-inset ring-[#299c8f] transition-all hover:scale-[1.02] hover:shadow-md active:scale-[0.98]';

/** Out to Online Giving's donor sign-in, where recurring gifts and payment methods are managed. */
function ManageGivingLink({ className = '' }: { className?: string }) {
  const { urls } = useProfile();
  return (
    <a
      href={urls.manageGiving}
      target="_blank"
      rel="noopener"
      onClick={(e) => e.stopPropagation()}
      className={`${CTA_OUTLINE} ${className}`}
    >
      Manage
      <ExternalLink className="h-4 w-4" strokeWidth={2.5} />
    </a>
  );
}

/**
 * Giving said small, for the login drawer: the year's total and its gifts,
 * then two ways on. "See all giving" is the full profile page with the Giving
 * sheet already open; "Manage" is OLG's donor sign-in.
 */
export function GivingMini({ profileUrl, onLeave }: { profileUrl: string; onLeave?: () => void }) {
  const { giving } = useGiving();
  const last = giving && giving !== 'error' ? giving.donations[0] : undefined;
  return (
    <div className={`${CARD} relative overflow-clip p-5`}>
      <span
        aria-hidden
        className="pointer-events-none absolute -top-16 -right-12 h-44 w-44 rounded-full opacity-20 blur-3xl"
        style={{ background: 'radial-gradient(closest-side, #299c8f, transparent)' }}
      />
      <div className="relative">
        {giving === null ? (
          <div className="flex flex-col gap-2" aria-busy="true">
            <Bar className="h-3 w-24" />
            <Bar className="h-10 w-36" />
          </div>
        ) : giving === 'error' ? (
          <p className="text-sm text-gray-500">We couldn't load your giving right now.</p>
        ) : giving.years.length === 0 ? (
          <p className="text-sm text-gray-500">No gifts on record yet.</p>
        ) : (
          <>
            <div className="flex items-center justify-between gap-3">
              <p className="text-[11px] font-bold tracking-widest text-gray-500 uppercase">
                Given in {giving.year}
              </p>
              <HideAmountsToggle />
            </div>
            <p className={`${DISPLAY} mt-1 text-4xl leading-none text-[#1c2b39]`}>
              <Money value={giving.total} />
            </p>
            <p className="mt-1.5 text-sm text-gray-500">
              {giving.donations.length === 0
                ? 'Nothing yet this year.'
                : `${giving.donations.length} ${giving.donations.length === 1 ? 'gift' : 'gifts'}${
                    last ? ` · last on ${fmtShortDate(last.date)}` : ''
                  }`}
            </p>
          </>
        )}
        <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2">
          <a href={withOpenParam(profileUrl, 'giving')} onClick={onLeave} className={CTA_TEAL}>
            See all giving
            <ChevronRight className="h-4 w-4" strokeWidth={2.5} />
          </a>
          <ManageGivingLink />
        </div>
      </div>
    </div>
  );
}
